import React from "react";
import {
  Drawer,
  Tag,
  Collapse,
  Table,
  Progress,
  Typography,
  Spin,
  Empty,
  Space,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetLevelCCandidateDetail } from "../../hooks/levelc-reports";
import type {
  LevelCCandidateCompetency,
  LevelCQuestionScore,
} from "../../types/levelc-reports";

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface LevelCCandidateDetailProps {
  examInstanceId: string | null;
  open: boolean;
  onClose: () => void;
}

export const LevelCCandidateDetail: React.FC<LevelCCandidateDetailProps> = ({
  examInstanceId,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data: candidate, isLoading, error } = useGetLevelCCandidateDetail({
    examInstanceId,
    enabled: open && !!examInstanceId,
  });

  const questionColumns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 50,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: t("levelCReports.candidateDetail.questionType"),
      dataIndex: "questionType",
      key: "questionType",
      width: 120,
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: t("levelCReports.candidateDetail.score"),
      key: "score",
      width: 120,
      render: (record: LevelCQuestionScore) => (
        <Text>
          {record.score !== null ? record.score.toFixed(1) : "-"} / {record.maxScore.toFixed(1)}
        </Text>
      ),
    },
    {
      title: t("levelCReports.candidateDetail.result"),
      key: "result",
      width: 100,
      render: (record: LevelCQuestionScore) => {
        if (record.isCorrect === null) {
          return (
            <Tooltip title={t("levelCReports.candidateDetail.notGraded")}>
              <QuestionCircleOutlined style={{ color: "#faad14", fontSize: 18 }} />
            </Tooltip>
          );
        }
        return record.isCorrect ? (
          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
        );
      },
    },
    {
      title: t("levelCReports.candidateDetail.gradedBy"),
      dataIndex: "gradedBy",
      key: "gradedBy",
      ellipsis: true,
      render: (gradedBy: string | null, record: LevelCQuestionScore) => {
        if (!gradedBy) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={record.gradedAt ? dayjs(record.gradedAt).format("YYYY-MM-DD HH:mm") : ""}>
            <Space>
              <UserOutlined />
              <Text>{gradedBy}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
  ];

  const renderCompetencyPanel = (competency: LevelCCandidateCompetency) => {
    const percentage = competency.maxScore > 0
      ? (competency.score / competency.maxScore) * 100
      : 0;
    const status = competency.hasPassed ? "success" : "exception";

    return (
      <Panel
        key={competency.competencyId}
        header={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space>
              {competency.hasPassed ? (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              <Text strong>{competency.competencyName}</Text>
            </Space>
            <Space>
              <Text type="secondary">
                {competency.score.toFixed(1)} / {competency.maxScore.toFixed(1)}
              </Text>
              <Progress
                percent={Math.round(percentage)}
                size="small"
                status={status}
                style={{ width: 100, margin: 0 }}
                format={(percent) => `${percent}%`}
              />
            </Space>
          </Space>
        }
      >
        <Table
          columns={questionColumns}
          dataSource={competency.questions}
          rowKey="questionId"
          pagination={false}
          size="small"
          bordered
        />
      </Panel>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
        </div>
      );
    }

    if (error || !candidate) {
      return (
        <Empty
          description={t("levelCReports.candidateDetail.notFound")}
          style={{ padding: "60px 0" }}
        />
      );
    }

    const overallPercentage = candidate.maxScore > 0
      ? (candidate.totalScore / candidate.maxScore) * 100
      : 0;

    const competencyPercentage = candidate.competenciesTotal > 0 
      ? Math.round((candidate.competenciesPassed / candidate.competenciesTotal) * 100) 
      : 0;

    const sortedCompetencies = [...candidate.competencies].sort((a, b) =>
      a.competencyName.localeCompare(b.competencyName, undefined, { numeric: true }));

    return (
      <div>
        {/* Header section */}
        <div style={{ marginBottom: 24 }}>
          <Title level={4} style={{ marginBottom: 16 }}>
            <Space>
              <UserOutlined />
              {candidate.candidateName}
            </Space>
          </Title>

          {/* Info Grid */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: 16,
            marginBottom: 16 
          }}>
            {/* Exam Card */}
            <div style={{ 
              background: "#fafafa", 
              borderRadius: 8, 
              padding: "12px 16px",
              border: "1px solid #f0f0f0"
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                {t("levelCReports.candidateDetail.exam")}
              </Text>
              <Tooltip title={candidate.examTitle}>
                <Text strong ellipsis style={{ display: "block" }}>
                  {candidate.examTitle}
                </Text>
              </Tooltip>
            </div>

            {/* Term/Date Card */}
            <div style={{ 
              background: "#fafafa", 
              borderRadius: 8, 
              padding: "12px 16px",
              border: "1px solid #f0f0f0"
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                {t("levelCReports.candidateDetail.term")}
              </Text>
              <Tooltip title={dayjs(candidate.examDate).format("dddd, MMMM D, YYYY")}>
                <Space size={4}>
                  <CalendarOutlined />
                  <Text strong>{dayjs(candidate.examDate).format("MMM D, YYYY")}</Text>
                </Space>
              </Tooltip>
            </div>

            {/* Result Card */}
            <div style={{ 
              background: "#fafafa", 
              borderRadius: 8, 
              padding: "12px 16px",
              border: "1px solid #f0f0f0"
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                {t("levelCReports.candidateDetail.result")}
              </Text>
              {candidate.hasPassed === null ? (
                <Tag color="default">{t("levelCReports.gradingStatus.inProgress")}</Tag>
              ) : candidate.hasPassed ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  {t("levelCReports.candidates.statusPassed")}
                </Tag>
              ) : (
                <Tag color="error" icon={<CloseCircleOutlined />}>
                  {t("levelCReports.candidates.statusFailed")}
                </Tag>
              )}
            </div>
          </div>

          {/* Score Summary Row */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
            gap: 16 
          }}>
            {/* Overall Score Card */}
            <div style={{ 
              background: "#f6ffed", 
              borderRadius: 8, 
              padding: "16px 20px",
              border: "1px solid #b7eb8f",
              display: "flex",
              alignItems: "center",
              gap: 12
            }}>
              <TrophyOutlined style={{ fontSize: 28, color: "#52c41a" }} />
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  {t("levelCReports.candidateDetail.overallScore")}
                </Text>
                <Text strong style={{ fontSize: 20 }}>
                  {candidate.totalScore.toFixed(1)}/{candidate.maxScore.toFixed(1)}
                </Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  ({overallPercentage.toFixed(0)}%)
                </Text>
              </div>
            </div>

            {/* Competencies Passed Card */}
            <div style={{ 
              background: "#e6f7ff", 
              borderRadius: 8, 
              padding: "16px 20px",
              border: "1px solid #91d5ff",
              display: "flex",
              alignItems: "center",
              gap: 12
            }}>
              <Progress
                type="circle"
                percent={competencyPercentage}
                size={50}
                status={candidate.competenciesPassed === candidate.competenciesTotal ? "success" : "normal"}
                format={(percent) => <span style={{ fontSize: 12 }}>{percent}%</span>}
              />
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  {t("levelCReports.candidateDetail.competenciesPassed")}
                </Text>
                <Text strong style={{ fontSize: 20 }}>
                  {candidate.competenciesPassed}/{candidate.competenciesTotal}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Competencies breakdown */}
        <Title level={5} style={{ marginBottom: 16 }}>
          {t("levelCReports.candidateDetail.competencyBreakdown")}
        </Title>

        {sortedCompetencies.length > 0 ? (
          <Collapse
            accordion
            defaultActiveKey={sortedCompetencies[0]?.competencyId}
          >
            {sortedCompetencies.map(renderCompetencyPanel)}
          </Collapse>
        ) : (
          <Empty description={t("levelCReports.candidateDetail.noCompetencies")} />
        )}
      </div>
    );
  };

  return (
    <Drawer
      title={t("levelCReports.candidateDetail.title")}
      placement="right"
      width={800}
      onClose={onClose}
      open={open}
      destroyOnClose
      styles={{ body: { paddingTop: 16 } }}
    >
      {renderContent()}
    </Drawer>
  );
};

